/**
 * Vendors page — data tables.
 *
 * Pulled out of the JSX so the page file reads as composition only
 * and the marketing copy is editable without scrolling through layout.
 */
import {
  Network, Building2, TrendingUp, Workflow, Repeat2, BarChart3,
  ShieldCheck, Boxes, Layers, ClipboardList,
  Microscope, HomeIcon, Activity, Pill, Sparkles, Stethoscope,
  Truck, HeartPulse,
} from "lucide-react";

export const VENDOR_PROBLEMS = [
  "Fragmented clinic relationships",
  "Inconsistent ordering volume",
  "Lack of standardized deployment",
  "Operational friction across clinics",
  "Heavy reliance on direct sales",
  "Poor visibility into usage and performance",
  "Difficulty supporting recurring utilization",
];

export const SOLUTION_NODERA_PROVIDES = [
  "Packaged into clear programs",
  "Deployed through standardized workflows",
  "Supported by automation",
  "Aligned with recurring utilization models",
  "Integrated into clinic operations",
];

export const SOLUTION_VENDOR_OUTCOMES = [
  { icon: Building2, t: "Access to clinics actively deploying cash-pay programs" },
  { icon: Workflow,  t: "Standardized workflows that reduce variability" },
  { icon: Repeat2,   t: "Program-based utilization (not one-off orders)" },
  { icon: Layers,    t: "Operational coordination layer" },
  { icon: BarChart3, t: "Data visibility and performance tracking" },
];

export const HOW_IT_WORKS_STEPS = [
  { n: "01", title: "Onboarding & qualification", icon: ShieldCheck, items: ["Compliance verification", "Service validation", "Operational alignment"] },
  { n: "02", title: "Platform integration",       icon: Workflow,    items: ["Ordering processes", "Fulfillment coordination", "Communication systems"] },
  { n: "03", title: "Program packaging",          icon: Boxes,       items: ["Embedded into clinic-ready programs", "Aligned with demand categories"] },
  { n: "04", title: "Clinic deployment",          icon: Building2,   items: ["Clinics activate programs", "Workflows drive utilization"] },
  { n: "05", title: "Utilization & optimization", icon: TrendingUp,  items: ["Recurring demand", "Operational consistency", "Performance insights"] },
];

export const VALUE_PILLARS = [
  { icon: Building2, title: "Qualified distribution", items: ["Access to clinics actively launching programs", "Reduced need for cold sales"] },
  { icon: Repeat2,   title: "Structured demand",      items: ["Services embedded into programs", "Recurring utilization vs. sporadic orders"] },
  { icon: Workflow,  title: "Operational efficiency", items: ["Standardized workflows", "Reduced onboarding friction per clinic"] },
  { icon: Network,   title: "Scalable growth",        items: ["Expand across multiple clinics through one integration", "Leverage shared infrastructure"] },
  { icon: BarChart3, title: "Visibility & insights",  items: ["Performance tracking", "Usage trends", "Program-level insights (non-clinical)"] },
];

export const VENDOR_PROFILES = [
  { icon: Microscope,  label: "Diagnostic labs" },
  { icon: HomeIcon,    label: "Home testing providers" },
  { icon: Activity,    label: "Remote monitoring" },
  { icon: Pill,        label: "Compounding pharmacies (where compliant)" },
  { icon: HeartPulse,  label: "Wellness service providers" },
  { icon: Sparkles,    label: "Digital health tools" },
  { icon: Truck,       label: "Fulfillment / logistics partners" },
  { icon: Stethoscope, label: "Clinical support services" },
];

export const SELECTION_FILTERS = [
  "Operate within regulatory requirements",
  "Maintain independent compliance",
  "Support consistent service delivery",
  "Align with program-based utilization models",
];

export const ECONOMICS_BENEFITS = [
  "Clinics acquire services directly from vendors",
  "Clinics access competitive Nodera-contracted rates",
  "Clinics keep the patient relationship + price-setting",
  "Vendors maintain direct relationships with clinics",
];

export const ECONOMICS_DISCLAIMERS = [
  "Nodera does not take ownership of medical services",
  "Nodera does not participate in clinical pricing decisions",
  "Nodera does not custody funds",
  "Vendors and clinics maintain independent business relationships",
];

export const BOUNDARIES = [
  "Does not practice medicine",
  "Does not employ providers",
  "Does not prescribe or recommend treatments",
  "Does not influence clinical decisions",
  "Does not act as a distributor or reseller",
  "Does not control vendor pricing",
];

export const GETTING_STARTED_STEPS = [
  { n: "01", t: "Submit application",     icon: ClipboardList },
  { n: "02", t: "Qualification & review", icon: ShieldCheck },
  { n: "03", t: "Integration planning",   icon: Layers },
  { n: "04", t: "Platform onboarding",    icon: Workflow },
  { n: "05", t: "Go live with clinics",   icon: TrendingUp },
];

export const NETWORK_DIAGRAM_VENDORS = ["Diagnostics Lab", "Home Testing", "Remote Monitoring", "Wellness Co."];
export const NETWORK_DIAGRAM_CLINICS = ["Wellness Clinic", "Concierge Practice", "Med Spa", "Functional Med."];
export const NETWORK_DIAGRAM_TRUST_CHIPS = [
  { k: "Curated",    v: "Network" },
  { k: "Stringent",  v: "Vetting" },
  { k: "Contracted", v: "Rates" },
];
